import { inject, Injectable } from '@angular/core';
import { CalendarIntegrationStore, ViewSettingsStore } from '@app/_stores';
import { SettingsService } from '../settings.service';
import { DependencyService } from '../dependency.service';
import { MatDialog } from '@angular/material/dialog';
import { EntityStatus } from '@app/_types/generic.type';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { MapService } from '../map.service';
import { CalendarEvent, CalendarEventSetup, CalendarIntegration } from '@app/_interfaces/calendar-integration.interface';
import { CacheService } from '../cache.service';
import { log } from '@app/_services/logger';
import { getEntityVersion } from '@app/_configs/entity-versions';
import { ConsolidatedApiService } from '../consolidated-api.service';
import { environment } from '@environments/environment';
import { getAuth } from 'firebase/auth';

@Injectable({
    providedIn: 'root',
})

export class CalendarIntegrationService {

    readonly calendarStore = inject(CalendarIntegrationStore);
    readonly viewSettingsStore = inject(ViewSettingsStore);

    private readonly googleCalendarClientId = environment.googleCalendarClientId;
    private readonly microsoftCalendarClientId = environment.microsoftCalendarClientId;

    constructor(
        public ss: SettingsService,
        public ds: DependencyService,
        public dialog: MatDialog,
        public mapService: MapService,
        public cc: CacheService,
        private consolidatedApi: ConsolidatedApiService,
    ) { }

    // ========================================================================
    // OAuth Authentication (shared by CalendarConnect + CalendarAccountForm)
    // Matches Flutter's CalendarConfigService pattern
    // ========================================================================

    /**
     * Trigger Google OAuth and return credentials payload.
     * Returns null if user cancels or is not authenticated.
     */
    connectGoogle(): Promise<any> {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            log.error('CalendarIntegrationService', 'User not authenticated with Firebase');
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            if (!document.getElementById('google-identity-script')) {
                const script = document.createElement('script');
                script.id = 'google-identity-script';
                script.src = 'https://accounts.google.com/gsi/client';
                script.onload = () => this.initGoogleOAuth(resolve);
                document.head.appendChild(script);
            } else {
                this.initGoogleOAuth(resolve);
            }
        });
    }

    private initGoogleOAuth(resolve: (value: any) => void) {
        // @ts-ignore - Google Identity Services
        const client = google.accounts.oauth2.initCodeClient({
            client_id: this.googleCalendarClientId,
            scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events email profile',
            ux_mode: 'popup',
            redirect_uri: 'postmessage',
            callback: (response: { code: string | null }) => {
                if (response.code) {
                    resolve({
                        serverAuthCode: response.code,
                        type: 'google',
                        uid: this.cc.user.uid || '',
                        redirectUri: 'postmessage',
                    });
                } else {
                    resolve(null);
                }
            }
        });
        client.requestCode();
    }

    /**
     * Trigger Microsoft OAuth and return credentials payload.
     * Returns null if user cancels or is not authenticated.
     */
    connectMicrosoft(): Promise<any> {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            log.error('CalendarIntegrationService', 'User not authenticated with Firebase');
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            const domain = window.location.origin;
            const state = crypto.randomUUID();
            const redirectUri = `${domain}/me-calendar-auth`;

            const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` + new URLSearchParams({
                client_id: this.microsoftCalendarClientId,
                response_type: 'code',
                redirect_uri: redirectUri,
                scope: 'openid email User.Read offline_access Calendars.ReadWrite',
                response_mode: 'query',
                state: state,
                prompt: 'select_account'
            }).toString();

            const width = 500;
            const height = 600;
            const left = window.screenX + (window.innerWidth - width) / 2;
            const top = window.screenY + (window.innerHeight - height) / 2;
            window.open(authUrl, '_blank', `width=${width},height=${height},left=${left},top=${top}`);

            const handler = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                const { code, receivedState } = event.data || {};
                if (code && receivedState === state) {
                    window.removeEventListener('message', handler);
                    resolve({
                        serverAuthCode: code,
                        type: 'microsoft',
                        uid: user.uid,
                        redirectUri: redirectUri,
                    });
                }
            };
            window.addEventListener('message', handler);
        });
    }

    /**
     * Call backend to exchange OAuth credentials for calendar details.
     * Returns parsed CalendarIntegration or throws on error.
     */
    async getCalendarDetails(credentials: any): Promise<CalendarIntegration> {
        const result = await this.consolidatedApi.call<{ data: string }>('getCalendarDetails', credentials);
        if (result.error) {
            throw new Error(result.error);
        }
        return JSON.parse(result.data!.data);
    }

    getStatus(setupId: string, dateString: string): EntityStatus {
        const setup = this.calendarStore.idToEventSetup()[setupId];
        if (!setup) return 'none';
        const todayDateString = getDateString();
        // Use the action's startAt when one exists for this date (e.g. a rescheduled exception
        // has a different time than the original setup). Falls back to the setup's startAt.
        const eventOnDate = this.calendarStore.setupToEventsMap()[setupId]?.[dateString];
        const effectiveStartTime = eventOnDate?.startTime ?? setup.startTime;
        const [year, month, day] = dateString.split('-').map(Number);
        const [hours, minutes] = (effectiveStartTime || '00:00').split(':').map(Number);
        const localDate = new Date(year, month - 1, day, hours, minutes);
        const isOverdue = localDate < new Date();
        const isCompleted = (setup.startDate && setup.startDate < todayDateString && setup.endDate && setup.endDate < todayDateString) || (setup.startDate && setup.startDate < todayDateString && !setup.repeat) || isOverdue;
        return isCompleted ? 'completed' : 'none';
    };

    setupToCalendarEvents(setup: CalendarEventSetup): CalendarEvent {
        const event: CalendarEvent = {
            id: setup.id,
            title: setup.title,
            description: setup.description,
            calendarId: setup.calendarId,
            calendarName: setup.calendarName,
            calendarIntegrationId: setup.calendarIntegrationId,
            startDate: setup.startDate,
            startTime: setup.startTime,
            timeZoneId: setup.timeZoneId,
            endDate: setup.endDate,
            cloudUpdatedAt: setup.cloudUpdatedAt,
            isAllDay: setup.isAllDay,
            duration: setup.duration,
            reminderAt: setup.reminderAt,
            repeat: setup.repeat,
            location: setup.location,
            setupId: setup.id,
            dueDate: setup.startDate || '',
            originalDueDate: null,
            rawData: setup.rawData,
            createdAt: new Date(),
            deletedAt: null,
            localUpdatedAt: new Date(),
            permaDeletedAt: null,
            entityVer: getEntityVersion('calendarEvents'),
            entityType: 'calendarEvents',
            source: 'client',
            sessionId: '1',
            uid: this.cc.user.uid,
            calendarAccountId: setup.calendarAccountId,
            conferenceData: null,
            attachments: setup.attachments,
        };
        return event;
    }
}
